/**
 * Playful Theme Registry
 *
 * Const object registry pattern matching the color theme system.
 * SYNC: Keep in sync with PlayfulThemeId enum in identifiers.ts
 */

import { PlayfulThemeDefinition } from './types';
import { PlayfulThemeId } from './identifiers';
import { offTheme } from './themes/off.theme';
import { spaceTheme } from './themes/space.theme';
import { oceanWavesTheme } from './themes/ocean-waves.theme';
import { jungleTheme } from './themes/jungle.theme';
import { candyTheme } from './themes/candy.theme';
import { robotTheme } from './themes/robot.theme';
import { magicTheme } from './themes/magic.theme';
import { doodleTheme } from './themes/doodle.theme';

export const REGISTERED_PLAYFUL_THEMES = {
  [PlayfulThemeId.OFF]: offTheme,
  [PlayfulThemeId.SPACE]: spaceTheme,
  [PlayfulThemeId.OCEAN_WAVES]: oceanWavesTheme,
  [PlayfulThemeId.JUNGLE]: jungleTheme,
  [PlayfulThemeId.CANDY]: candyTheme,
  [PlayfulThemeId.ROBOT]: robotTheme,
  [PlayfulThemeId.MAGIC]: magicTheme,
  [PlayfulThemeId.DOODLE]: doodleTheme,
} as const satisfies Record<string, PlayfulThemeDefinition>;

export type PlayfulThemeName = keyof typeof REGISTERED_PLAYFUL_THEMES;

export function getPlayfulTheme(
  name: PlayfulThemeName
): PlayfulThemeDefinition {
  return REGISTERED_PLAYFUL_THEMES[name];
}

export function getAllPlayfulThemes(): Array<
  PlayfulThemeDefinition & { name: PlayfulThemeName }
> {
  return Object.entries(REGISTERED_PLAYFUL_THEMES).map(([name, def]) => ({
    ...def,
    name: name as PlayfulThemeName,
  }));
}

export function hasPlayfulTheme(name: string): name is PlayfulThemeName {
  return name in REGISTERED_PLAYFUL_THEMES;
}

export function getAllMascots(): Array<{
  themeId: PlayfulThemeName;
  emoji: string;
  label: string;
}> {
  return Object.entries(REGISTERED_PLAYFUL_THEMES)
    .filter(([, def]) => def.metadata.mascotEmoji !== '')
    .map(([name, def]) => ({
      themeId: name as PlayfulThemeName,
      emoji: def.metadata.mascotEmoji,
      label: def.metadata.label,
    }));
}
